import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AcceptInviteService, InviteVerificationDetails } from './accept-invite.service';
import { MeService } from '../../global-services/me.service';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { GlobalLoadingService } from '../../global-services/global-loading.service';

export interface AcceptInviteUiState {
  token: string | null;
  inviteDetails: InviteVerificationDetails | null;
  isUserAuthenticated: boolean;
  error?: string;
  accepted: boolean;
}

@Injectable({ providedIn: 'root' })
export class AcceptInviteFacade {
  private uiStateSubject = new BehaviorSubject<AcceptInviteUiState>({
    token: null,
    inviteDetails: null,
    isUserAuthenticated: false,
    error: undefined,
    accepted: false,
  });
  uiState$ = this.uiStateSubject.asObservable();

  constructor(
    private acceptInviteService: AcceptInviteService,
    private meService: MeService,
    private router: Router,
    private messageService: GlobalMessageService,
    private loading: GlobalLoadingService,
  ) { }

  async load(token: string): Promise<void> {
    this.loading.show();
    this.uiStateSubject.next({
      ...this.uiStateSubject.value,
      token,
      inviteDetails: null,
      isUserAuthenticated: false,
      error: undefined,
      accepted: false,
    });

    try {
      const inviteDetails = await this.acceptInviteService.verifyInvite(token);
      const me = await this.meService.refreshMe();
      const isAuthenticated = me !== null && me !== undefined;

      if (isAuthenticated && me?.email !== inviteDetails.inviteEmail) {
        this.messageService.showError('Error', 'You are already signed in with a different email to the email the invite was sent from.');
        this.uiStateSubject.next({
          ...this.uiStateSubject.value,
          inviteDetails: null,
          error: 'The account email does not match the invite email. Try signing out and signing in with the account that matches the email you receieved the email from invite',
          accepted: false,
        });
        return;
      }

      this.uiStateSubject.next({
        token,
        inviteDetails,
        isUserAuthenticated: isAuthenticated,
        error: undefined,
        accepted: false,
      });
    } catch (error) {
      const message = (error as any)?.error?.message || (error as any)?.message || 'Failed to verify invite token.';
      this.messageService.showError('Error', message);
      this.uiStateSubject.next({
        ...this.uiStateSubject.value,
        inviteDetails: null,
        error: message,
        accepted: false,
      });
    } finally {
      this.loading.hide();
    }
  }

  async inviteCancelled(): Promise<void> {
    this.router.navigate(['/home']);
  }

  async acceptInvite(): Promise<void> {
    const currentState = this.uiStateSubject.value;
    if (!currentState.token) return;

    this.loading.show();
    try {
      await this.acceptInviteService.acceptInvite(currentState.token);
      this.messageService.showSuccess('Invite Accepted', 'You have successfully joined the organisation.');
      await this.meService.refreshMe();
      this.uiStateSubject.next({ ...currentState, accepted: true, error: undefined });
      this.router.navigate(['/home']);
    } catch (error) {
      const message = (error as any)?.error?.message || (error as any)?.message || 'Failed to accept invite.';
      this.messageService.showError('Error', message);
      this.uiStateSubject.next({ ...currentState, accepted: false, error: message });
    }
    this.loading.hide();
  }

  navigateToSignIn(): void {
    const token = this.uiStateSubject.value.token;
    const email = this.uiStateSubject.value.inviteDetails?.inviteEmail;
    const emailUrlEncoded = encodeURIComponent(email || '');

    if (token) {
      this.router.navigate(['/sign-in'], { queryParams: { inviteToken: token, email: emailUrlEncoded } });
    }
  }

  navigateToSignUp(): void {
    const token = this.uiStateSubject.value.token;
    const email = this.uiStateSubject.value.inviteDetails?.inviteEmail;
    const emailUrlEncoded = encodeURIComponent(email || '');

    if (token) {
      this.router.navigate(['/sign-up'], { queryParams: { inviteToken: token, email: emailUrlEncoded } });
    }
  }
}
